import { inject, Injectable } from '@angular/core';
import { DataService } from 'src/app/data.service';
import { ARBEITSZEIT, BaseHelpService } from '@luzula/luzula-lib';
import { festigkeitHilfetext } from './festigkeit-stufen';
import { muddeKonsistenzHilfetext } from './mudde-konsistenz-stufen';
import { substanzvolumenHilfetext } from './substanzvolumen-stufen';
import { feindurchwurzelungHilfetext } from './feindurchwurzelung-stufen';
import { tiefeHilfetext } from '../filz-tiefen';

@Injectable({
  providedIn: 'root'
})
export class HelpService extends BaseHelpService {
  private ds = inject(DataService);

  constructor() {
    super();
    // Hauptbodenform und Feinbodenform haben KEIN (i) mehr: Katalog und
    // Auswahlliste sind Knoepfe im Feld (BodenformNachschlagenService,
    // Nutzer-Entscheidung 28.09.2026). Das (i) war dort keine Erklaerung.
    // Moorsubstrat-Referenztabelle (wird über Extension-Handler als Modal angezeigt)
    this.registerPicker('Moorsubstrat');
    // Torf-/Muddearten-Referenztabelle (wird über Extension-Handler als Modal angezeigt)
    this.registerPicker('TorfMuddeArten');
    // Lade BodenformSuffixe Namen als Liste
    const suffixe = this.ds.getHardcoded('BodenformSuffixe') as {value: string, name: string}[];
    if (suffixe) {
      this.registerList('BodenformSuffix', suffixe);
    }

    // Lade Torfarten als Liste
    const torfarten = this.ds.getHardcoded('Torfarten') as {value: string, name: string}[];
    if (torfarten) {
      this.registerList('Torfart', torfarten);
    }

    // Lade Farben als Liste
    const farben = this.ds.getHardcoded('Farben') as {value: string, name: string}[];
    if (farben) {
      this.registerList('Farbe', farben);
    }

    // Zersetzungsgrad-Referenztabelle (wird über Extension-Handler als Modal angezeigt)
    this.registerPicker('Zersetzungsgrad');

    // Substratfolge-Hilfen: Mudde-Konsistenz und Substanzvolumen. Die Reihen
    // stehen in den *-stufen.ts-Dateien — dieselben, aus denen das Stufen-Feld
    // in der Substratfolge Farbe und Namen nimmt (seit 15.09.2026).
    this.registerText('Konsistenz', muddeKonsistenzHilfetext());
    this.registerText('Substanzvolumen', substanzvolumenHilfetext());

    // Substratfolge-Hilfen: Humusgehalt und Lagerungsdichte (B-Punkte).
    // Beide als eigene Fenster ueber den Extension-Handler: Der fruehere
    // Textblock zum Humusgehalt war um eine Stufe verschoben, der zur
    // Lagerungsdichte bestand aus drei Woertern.
    this.registerPicker('Humusgehalt');
    this.registerPicker('Lagerungsdichte');
    this.registerPicker('Skelettgehalt');
    this.registerPicker('Karbonat');

    // „Tiefe bis" in Substrat- und Horizontfolge: ab Oberkante Mineralboden,
    // auch bei einem Filz - dort rechnet die App die Auflage von selbst dazu
    // (filz-tiefen.ts, Nutzer-Entscheidung 25.09.2026).
    this.registerText('Tiefe', tiefeHilfetext());

    // Entwässerungsverhalten — erläutert den automatischen Vorschlag (Anlage 2).
    // Eigenes Fenster über den Extension-Handler (Gruppen, Regeln als Tabelle).
    this.registerPicker('Entwässerungsverhalten');

    // Relief-Hilfen (werden über Extension-Handler als Tabellen-Modal angezeigt)
    for (const key of ['Neigungsstufe', 'Woelbung', 'Hangrichtung', 'ReliefformUmgebung', 'Hanganschluss', 'Hangteil', 'Hoehenspanne', 'InnereRauhheit']) {
      this.registerPicker(key);
    }

    // Grund- und Stauwasserformen (SEA 95, Darst. A 3-2a / A 3-4a)
    // Werden über Extension-Handler als Matrix-Modal angezeigt
    // Keys entsprechen den Feld-name-Attributen: Wasserform = Grundwasser, Wasserform2 = Stauwasser
    this.registerPicker('Wasserform');
    this.registerPicker('Wasserform2');

    // Horizontfolgetypen, Ökolog./Hydrogen. Moortyp (SEA 95, Darst. A 2-5/A 2-6)
    // Werden über Extension-Handler als Tabellen-Modals angezeigt
    this.registerPicker('Horizontfolgetypen');
    this.registerPicker('OekologischerMoortyp');
    this.registerPicker('HydrogenetischerMoortyp');

    // Grubenprotokoll — Festigkeit (SEA 95, Teil B, Abschnitt 3.2.8.11, S. 76 f.).
    // Der Text kommt aus derselben Reihe wie die Ansprache im Fenster zur
    // Lagerungsdichte — zwei Fassungen liefen auseinander.
    this.registerText('Festigkeit', festigkeitHilfetext());

    // Grubenprotokoll — Gefüge (SEA Teil B; alle Substrate)
    this.registerText('Gefuege', `Gefüge — Form / Grösse / Ausprägung

Form:
  pol — polyedrisch
  krü — krümelig
  pla — plattig
  lam — lamellär
  Einzelkorn / Massiv / Verkittung / ungegliedert

Grösse:
  skl — sehr klein   kl — klein   m — mittel   gr — gross   sgr — sehr gross

Ausprägung:
  schwach · mäßig · stark`);

    // Grubenprotokoll — Feindurchwurzelung (SEA Teil B); Reihe in
    // feindurchwurzelung-stufen.ts, dieselbe wie im Stufen-Feld der Häufung.
    this.registerText('Feindurchwurzelung', feindurchwurzelungHilfetext());

    // Grubenprotokoll — Perstruktionszone
    this.registerText('Perstruktionszone', `Perstruktionszone (Bodenbildungs- bzw. Verwitterungszone)

α — frischer Mineralhorizont, kaum verändert
β — beginnende Verwitterung
γ — fortgeschrittene Verwitterung
δ — Tonbildung / -verlagerung
ε — Verbraunung / Oxidation
ζ — Podsolierung / Auswaschung
η — Endstadium, stark umgewandelt`);

    // Grubenprotokoll — Lage der Schicht im Profil
    this.registerText('SchichtLage', 'Lage der Schicht im Grubenprofil (z. B. oberer/mittlerer/unterer Profilteil).');

    // Grubenprotokoll — Kurz-Hinweise Kopffelder
    this.registerText('Klimabereich', 'Grossklimatischer Bereich (planar … subalpin), Hauptkategorie nach Höhenstufe und Wärmesumme.');
    this.registerText('Bestockungstyp', 'Bestockungstyp der Aufnahmefläche: Reinbestand, Mischbestand, Plenterwald, Lichtwald, Verjüngung, Bestockungslücke, Blöße.');
    this.registerText('Begründungsart', 'Art der Bestandesbegründung: Naturverjüngung, Naturanflug, Saat, Pflanzung, Stockausschlag, Wurzelbrut.');
    this.registerText('Mischform', 'Räumliche Mischform des Bestandes: Reinbestand, einzelstammweise, truppweise, gruppenweise, horstweise, flächig.');
    this.registerText('Waldfunktion', 'Waldfunktion(en) der Aufnahmefläche: Wirtschaftswald, Schutzwald (Boden/Wasser/Lawinen/Klima), Erholungswald, Naturschutz, Bannwald, Genressourcenwald.');
    this.registerText('ZustandsSTFG', 'Naturnähe-Zustand: naturnah · bedingt naturnah · kulturbetont · kulturbestimmt · naturfern.');
    this.registerText('HumusformGesamt', 'Humusform der gesamten Aufnahmefläche (z. B. Mull, Moder, Rohhumus).');
    this.registerText('HumusformMorphologisch', 'Morphologisch erkennbare Humusform am Profil (Auflagedifferenzierung).');
    this.registerText('Probestaemme', 'Probestämme der Aufnahmefläche: Art, Alter, Höhe, Durchmesser, Bonität (−5…+5), Qualität.');

    // Hilfetext für Punktnummer
    this.registerText('Nummer', 'Punktnummer frei eingeben, Knopf generiert Punktnummer aus Forstamt, Forstrevier, eingegebener Punktnummer und Kartierjahr.');

    // FBF-Präfix (pro Bodenform-Glied einzeln einstellbar): eigenes Fenster mit
    // Kürzel und Bedeutung über den Extension-Handler.
    this.registerPicker('FBFPrefix');

    // Hilfetext für Witterung (automatische Wetterdaten)
    this.registerText('Witterung', `Automatisch abgerufene Wetterdaten von Open-Meteo.

Knopf „Datum": Temperatur, Wind, Bewölkung und Luftfeuchte sind das Mittel der Arbeitszeit ${ARBEITSZEIT.von} bis ${ARBEITSZEIT.bis} Uhr am Aufnahmetag; der Himmel ist der häufigste Zustand in dieser Zeit. Der Niederschlag ist die Tagessumme.
Knopf „Jetzt": die aktuellen Werte; der Niederschlag ist die Summe von heute bis jetzt.

🌡️ Temperatur
  frostig (<0°C) · kalt (0–5°C) · kühl (5–12°C)
  mild (12–18°C) · warm (18–25°C) · heiß (>25°C)

💧 Feuchtigkeit (Tagesniederschlag)
  trocken (<1mm) · leicht feucht (1–5mm)
  feucht (5–10mm) · nach Regenperiode (>10mm)

☁️ Himmel (Bewölkung)
  heiter (<20%) · leicht bewölkt (20–50%)
  bewölkt (50–80%) · bedeckt (>80%)
  Bei Sonderwetter: Nebel, Regen, Schnee, Gewitter

💨 Wind
  windstill (<5 km/h) · leichter Wind (5–20 km/h)
  mäßiger Wind (20–40 km/h) · starker Wind (40–60 km/h)
  stürmisch (>60 km/h) — jeweils mit Himmelsrichtung

🌫️ Luftfeuchte (relativ)
  sehr trocken (<30%) · normal (30–60%)
  feucht (60–80%) · hohe Luftfeuchte (>80%)
  schwül nur bei ≥18°C

🌱 Bodenzustand (Niederschlag der letzten 3 Tage)
  trocken (<1mm) · leicht feucht (1–5mm)
  feucht (5–15mm) · nass (15–30mm) · durchnässt (>30mm)`);
  }
}
